import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dealer } from '../../../shared/types';
import { JOURNEY_STATE, useApplicationState } from '../../App.state';
import { loadingOrError } from '../../lib/ui/Loaders/GraphqlLoadingUtility';
import { RouterHelpers } from '../../router/router-helpers';

import { Step } from '../../../shared/interface';
import { getDealer, useGetDealer, useGetDealers } from '../../api/getDealers';
import { submitAnswers } from '../../api/submitAnswers';
import { adobeDataLayer } from '../../lib/adobe-analytics-data-layer/AdobeDataLayer';
import { DealerSelectionComponent } from './DealerSelectionComponent';
import { buildSubmitAnswersRequestBody } from '../../utils/submitAnswersRequest';

const DEALER_COUNT = 9;

export function DealerSelection({ isEditMode = false }: { isEditMode?: boolean }): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<Dealer>();
  const [showSelectionError, setShowSelectionError] = useState(false);
  const navigate = useNavigate();
  const { state, dispatch } = useApplicationState();
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(DEALER_COUNT);
  const [currentSearchWasSubmitted, setCurrentSearchWasSubmitted] = useState(false);

  const {
    data: preSelectedDealer,
    error: preSelectedDealerError,
    isFetching: preSelectedDealerIsFetching
  } = useGetDealer(state.preSelectedDealerId, {
    enabled: Boolean(state.preSelectedDealerId)
  });

  useEffect(() => {
    if (search === '' && !preSelectedDealerIsFetching) {
      if (preSelectedDealer?.id !== state.dealer?.id) {
        setSearch(state.dealer?.zip ?? '');

        if (selectedDealer?.id !== state.dealer?.id) {
          setSelectedDealer(state.dealer);
        }
      }
    }
  }, [preSelectedDealer, search, preSelectedDealerIsFetching]);

  const { data: dealers, error, isFetching } = useGetDealers(search, { enabled: search !== '' });

  useEffect(() => {
    dispatch({ type: 'updateJourneyStatus', journeyStatus: JOURNEY_STATE.DEALER_SELECTION });
    adobeDataLayer.pageViewDealerSelectionInitial();
  }, [dispatch]);

  const showMoreButtonVisible = dealers ? dealers.length > visibleCount : false;

  if (isFetching || error || preSelectedDealerIsFetching || preSelectedDealerError) {
    if (error || preSelectedDealerError) {
      adobeDataLayer.pageViewErrorPage({
        errorCode: '400',
        errorMessage: 'Technical error'
      });
    }

    return loadingOrError(
      isFetching || preSelectedDealerIsFetching,
      error || preSelectedDealerError ? new Error('Something went wrong') : undefined
    );
  }

  const onSearchDealer = async (input?: string) => {
    if (input) {
      dispatch({ type: 'updateDealer', dealer: undefined });
      setSelectedDealer(undefined);
      setShowSelectionError(false);
      setSearch(input);
      setVisibleCount(DEALER_COUNT);
      adobeDataLayer.pageViewDealerSelectionPostSearch();
    }
  };

  const loadMore = () => {
    if (dealers) {
      let newVisibleCount = visibleCount + DEALER_COUNT;

      if (newVisibleCount >= dealers.length) {
        newVisibleCount = dealers.length;
      }

      setVisibleCount(newVisibleCount);
    }
  };

  async function continueWithDealer(dealer: Dealer) {
    setIsLoading(true);

    // Error below indicates that v4 API from which we ultimately fetch dealer
    // data had no email which is necessary to for entire CJ flow to work out.
    try {
      await getDealer(dealer.id);
    } catch {
      console.error('Fetching dealer from v4 failed or contact data was misisng.');
      setIsLoading(false);
      return;
    }

    submitAnswers(
      buildSubmitAnswersRequestBody({
        state,
        currentStep: Step.DEALER_SELECTION,
        selectedDealer: dealer.id
      })
    )
      .then((data) => {
        dispatch({ type: 'updateState', state: { ...state, transactionId: data, dealer } });

        if (state.preSelectedDealerId && dealer.id === state.preSelectedDealerId) {
          adobeDataLayer.selectDealer(dealer, true);
        } else {
          adobeDataLayer.selectDealer(dealer, false);
        }

        if (isEditMode) navigate(RouterHelpers.getSummaryPagePath());
        else {
          dispatch({ type: 'updateJourneyStatus', journeyStatus: JOURNEY_STATE.CONTACT_DETAILS });
          navigate(RouterHelpers.getPersonalDetailsPath());
        }
      })
      .catch((e) => {
        console.error('Error while submitting answers: ', e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleSelectDealer = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    dispatch({ type: 'updateDealer', dealer });
    setShowSelectionError(false);
  };

  const handleSearchInputChange = () => {
    setCurrentSearchWasSubmitted(false);
    setShowSelectionError(false);
  };

  const handleContinue = () => {
    const dealerToContinueWith = selectedDealer?.id ? selectedDealer : preSelectedDealer;

    if (!dealerToContinueWith?.id) {
      setShowSelectionError(true);
      return;
    }

    setShowSelectionError(false);
    continueWithDealer(dealerToContinueWith);
  };

  return (
    <DealerSelectionComponent
      prevCall={() => navigate(-1)}
      onFindDealer={onSearchDealer}
      onSearchInputChange={handleSearchInputChange}
      isLoading={isLoading}
      continueWithDealer={continueWithDealer}
      onContinue={handleContinue}
      onSelectDealer={handleSelectDealer}
      loadMore={loadMore}
      dealers={dealers}
      preselectedDealer={preSelectedDealer}
      selectedDealer={state.dealer}
      showSelectionError={showSelectionError}
      visibleCount={visibleCount}
      showMoreButtonVisible={showMoreButtonVisible}
      initialSearchTerm={search}
      currentSearchWasSubmitted={currentSearchWasSubmitted}
      setCurrentSearchWasSubmitted={setCurrentSearchWasSubmitted}
      isEditMode={isEditMode}
    />
  );
}
